import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Box, Chip, Container, Grid, Stack, Typography } from "@mui/material";
import GlobalHeader from "../../../components/GlobalHeader";
import SnackAlert from "../../../components/SnackAlert";
import useSnack from "../../../hooks/useSnack";
import useApi from "../../../hooks/useApi";
import { useEffect, useState } from "react";
import Audio from "react-loading-icons/dist/esm/components/audio";
import { Link } from "react-router-dom";
import { ESTILO_VENCIDO, etiquetaVencido } from "../../../utils/estadoEnvio";

const STATUS_LABELS = {
  pending: { label: "Pendiente", color: "warning" },
  approve: { label: "Aprobado", color: "success" },
  rejected: { label: "Rechazado", color: "error" },
  anulado: { label: "Anulado", color: "default" },
  // No lo respondiste a tiempo: el artista tiene unos días para reenviarlo.
  vencido: { label: "Vencido", color: "default" },
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },

  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export default function ViewArtistSends() {
  const [mainData, setMainData] = useState([]);
  const { msgSB, openSB, showSnack, typeSB } = useSnack();
  const { loadApi, loadedApi, loadingApi } = useApi();

  useEffect(() => {
    loadApi("productor/envios", true, "get")
      .then((response) => {
        setMainData(
          response.data.map((data) => {
            return {
              name: data.titulo || data.cancion,
              artista: data.artista,
              id: data.id,
              respuesta: data.status,
              songId: data.songId,
              reenviado: Boolean(data.reenviado_como),
              status: data.status,
              radarCoin: data.radarCoin,
              ajustes: data.ajustes_admin || [],
              correccion: data.correccion || null,
            };
          }) || []
        );
      })
      .catch((e) => {
        showSnack(e.message, "error", false);
      });
  }, []);

  return (
    <>
      <GlobalHeader />
      {!loadedApi.includes("get__productor/envios") && mainData.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100vw",
            height: `calc(100vh - ${
              window.document.getElementById("GlobalHeader")?.offsetHeight ||
              "64"
            }px - 32px)`,
          }}
        >
          <Audio fill="#F27224" />
        </Box>
      ) : (
        <Container>
          <Grid container spacing={5}>
            <Grid container item spacing={5}>
              <Grid item xs={12}>
                <Typography variant="h4" color="primary.main" fontWeight="900">
                  Historial de Envios
                </Typography>
              </Grid>
              <Grid item xs={12}>
                {mainData.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 670 }}>
                      {/* 1 seccion */}
                      <TableHead>
                        <TableRow>
                          <StyledTableCell align="center">
                            Cancion enviada
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            Artista
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            Estado
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            Respuesta
                          </StyledTableCell>
                        </TableRow>
                      </TableHead>

                      {/* 2 seccion */}
                      <TableBody>
                        {mainData.map((row, i) => (
                          <StyledTableRow key={i}>
                            <StyledTableCell component="th" align="center">
                              {/* Los eventos no tienen página de canción. */}
                              {row.songId ? (
                                <Link to={`/productor/cancion/${row.songId}/?envio=${row.id}`}>
                                  {row.name}
                                </Link>
                              ) : (
                                row.name
                              )}
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              {row.artista}
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              <Chip
                                label={
                                  row.status === "vencido"
                                    ? etiquetaVencido(row.reenviado)
                                    : STATUS_LABELS[row.status]?.label || row.status
                                }
                                color={
                                  STATUS_LABELS[row.status]?.color || "default"
                                }
                                variant={row.status === "vencido" && row.reenviado ? "outlined" : "filled"}
                                sx={row.status === "vencido" && !row.reenviado ? ESTILO_VENCIDO : undefined}
                                size="small"
                              />
                              {row.ajustes.map((ajuste, j) => (
                                <Box
                                  key={j}
                                  sx={{
                                    mt: 1,
                                    p: 1,
                                    textAlign: "left",
                                    borderLeft: "3px solid",
                                    borderColor: "primary.main",
                                    bgcolor: "rgba(242, 114, 36, 0.08)",
                                    maxWidth: 320,
                                    mx: "auto",
                                  }}
                                >
                                  <Typography variant="caption" component="div" fontWeight={700}>
                                    {ajuste.accion === "anular"
                                      ? "Anulado por el equipo"
                                      : ajuste.origen === "difusor"
                                        ? "Pasado a Envío Social: rechazaste la corrección"
                                        : ajuste.origen === "vencimiento"
                                          ? "Pasado a Envío Social: venció el plazo de corrección"
                                          : "Pasado a Envío Social por el equipo"}
                                    {ajuste.descontadoUSD > 0 &&
                                      ` · se descontaron ${ajuste.descontadoUSD} USD`}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    component="div"
                                    sx={{ whiteSpace: "pre-wrap" }}
                                  >
                                    {ajuste.mensaje}
                                  </Typography>
                                </Box>
                              ))}
                              <CorreccionEnCurso correccion={row.correccion} />
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              {row.respuesta === "pending"
                                ? "Aguardando respuesta"
                                : row.respuesta === "anulado"
                                  ? "Anulada por el equipo"
                                  : row.respuesta === "vencido"
                                    ? row.reenviado
                                      ? "El artista te la reenvió"
                                      : "Venció sin tu respuesta"
                                    : "Finalizada"}
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Stack alignItems="center">
                    <Typography variant="h5" mt={5} textAlign="center">
                      No hay envíos registrados
                    </Typography>
                  </Stack>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      )}
      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </>
  );
}

// Una corrección en curso: se responde desde Envíos.
function CorreccionEnCurso({ correccion }) {
  if (!correccion || !["solicitada", "aceptada", "entregada"].includes(correccion.estado))
    return null;
  return (
    <Box
      sx={{
        mt: 1,
        p: 1,
        textAlign: "left",
        borderLeft: "3px solid",
        borderColor: "warning.main",
        bgcolor: "rgba(242, 114, 36, 0.08)",
        maxWidth: 320,
        mx: "auto",
      }}
    >
      <Typography variant="caption" component="div" fontWeight={700}>
        {correccion.estado === "entregada"
          ? "🛠 Corrección entregada: el equipo la está revisando"
          : "🛠 El equipo te pidió una corrección"}
      </Typography>
      {correccion.estado !== "entregada" && (
        <Typography variant="caption" component="div">
          Tienes hasta el{" "}
          {new Date(correccion.vence).toLocaleString("es", {
            day: "numeric",
            month: "long",
            hour: "2-digit",
            minute: "2-digit",
          })}
          . <Link to="/productor/envios">Respóndela en Envíos</Link>.
        </Typography>
      )}
    </Box>
  );
}
